<template>
  <div class="ghl-contact-card">
    <div
      v-if="loading"
      class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
    </div>

    <div
      v-else-if="!supportContact"
      class="text-center py-8">
      <p class="text-gray-500 mb-4">
        No support contact assigned
      </p>
      <button
        class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium
               rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none
               focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        @click="openContactSelector">
        <svg
          class="mr-2 -ml-0.5 h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4" />
        </svg>
        Assign Support Contact
      </button>
    </div>

    <div
      v-else
      class="space-y-4">
      <div class="flex justify-between items-start">
        <div>
          <h4 class="text-lg font-medium text-gray-900">
            {{ supportContact.display_name }}
          </h4>
          <p
            v-if="supportContact.email"
            class="text-sm text-gray-500">
            {{ supportContact.email }}
          </p>
          <p
            v-if="supportContact.phone"
            class="text-sm text-gray-500">
            {{ supportContact.phone }}
          </p>
          <p
            v-if="supportContact.company_name"
            class="text-sm text-gray-500">
            {{ supportContact.company_name }}
          </p>
        </div>
        <button
          class="text-sm text-purple-600 hover:text-purple-500"
          @click="openContactSelector">
          Change
        </button>
      </div>

      <div
        v-if="supportContact.tags && supportContact.tags.length > 0"
        class="flex flex-wrap gap-2">
        <span
          v-for="tag in supportContact.tags"
          :key="tag"
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {{ tag }}
        </span>
      </div>

      <div class="border-t pt-4">
        <h5 class="text-sm font-medium text-gray-900 mb-2">
          Support Statistics
        </h5>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-500">
              Total Conversations
            </p>
            <p class="text-lg font-semibold text-gray-900">
              {{ conversationStats.total || 0 }}
            </p>
          </div>
          <div>
            <p class="text-sm text-gray-500">
              Avg Response Time
            </p>
            <p class="text-lg font-semibold text-gray-900">
              {{ conversationStats.avgResponseTime || 'N/A' }}
            </p>
          </div>
        </div>
      </div>

      <div class="border-t pt-4">
        <h5 class="text-sm font-medium text-gray-900 mb-2">
          Recent Support Tickets
        </h5>
        <div
          v-if="recentConversations.length > 0"
          class="space-y-2">
          <div
            v-for="conversation in recentConversations"
            :key="conversation.id"
            class="text-sm border-l-4 pl-3"
            :class="{
              'border-red-500': conversation.status === 'open' && conversation.unread_count > 0,
              'border-yellow-500': conversation.status === 'open' && conversation.unread_count === 0,
              'border-green-500': conversation.status === 'closed'
            }">
            <div class="flex justify-between">
              <span class="text-gray-600">{{ conversation.type }}</span>
              <span class="text-gray-500">{{ formatDate(conversation.last_message_at) }}</span>
            </div>
            <p class="text-xs text-gray-600">
              Status: <span class="font-medium">{{ conversation.status }}</span>
              <span
                v-if="conversation.assigned_to_user_name"
                class="ml-2">
                • Assigned to: {{ conversation.assigned_to_user_name }}
              </span>
            </p>
          </div>
        </div>
        <p
          v-else
          class="text-sm text-gray-500">
          No recent support tickets
        </p>
      </div>

      <div
        v-if="supportContact.last_activity_at"
        class="text-xs text-gray-500">
        Last activity: {{ formatDate(supportContact.last_activity_at) }}
      </div>

      <!-- Attribution Data Section -->
      <div
        v-if="hasAttributionData"
        class="border-t pt-4 mt-4">
        <h5 class="text-sm font-medium text-gray-900 mb-2">
          Attribution Data
        </h5>
        <div class="space-y-2">
          <!-- UTM fields are primary for support contacts -->
          <div
            v-if="supportContact.attribution?.utm_content"
            class="text-sm">
            <span class="text-gray-600">UTM Content:</span>
            <span class="ml-2 font-medium text-gray-900">{{ supportContact.attribution.utm_content }}</span>
          </div>
          <div
            v-if="supportContact.attribution?.utm_medium"
            class="text-sm">
            <span class="text-gray-600">UTM Medium:</span>
            <span class="ml-2 font-medium text-gray-900">{{ supportContact.attribution.utm_medium }}</span>
          </div>
          <div
            v-if="supportContact.attribution?.utm_campaign"
            class="text-sm">
            <span class="text-gray-600">UTM Campaign:</span>
            <span class="ml-2 font-medium text-gray-900">{{ supportContact.attribution.utm_campaign }}</span>
          </div>
          <div
            v-if="supportContact.attribution?.utm_source"
            class="text-sm">
            <span class="text-gray-600">UTM Source:</span>
            <span class="ml-2 font-medium text-gray-900">{{ supportContact.attribution.utm_source }}</span>
          </div>
          <div
            v-if="supportContact.attribution?.utm_term"
            class="text-sm">
            <span class="text-gray-600">UTM Term:</span>
            <span class="ml-2 font-medium text-gray-900">{{ supportContact.attribution.utm_term }}</span>
          </div>
          <!-- Also show non-UTM fields if present -->
          <div
            v-if="supportContact.attribution?.source"
            class="text-sm">
            <span class="text-gray-600">Source:</span>
            <span class="ml-2 font-medium text-gray-900">{{ supportContact.attribution.source }}</span>
          </div>
          <div
            v-if="supportContact.attribution?.medium"
            class="text-sm">
            <span class="text-gray-600">Medium:</span>
            <span class="ml-2 font-medium text-gray-900">{{ supportContact.attribution.medium }}</span>
          </div>
          <div
            v-if="supportContact.attribution?.session_source"
            class="text-sm">
            <span class="text-gray-600">Session Source:</span>
            <span class="ml-2 font-medium text-gray-900">{{ supportContact.attribution.session_source }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Selector Modal -->
    <ContactSelectorModal
      v-if="showSelector"
      :organization-id="organizationId"
      instance-type="support"
      @select="assignContact"
      @close="showSelector = false" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
// Date formatting utility
import ContactSelectorModal from './contact_selector_modal.vue'
import useGhlCache from '@/vue_widgets/composables/use_ghl_cache'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

const loading = ref(true)
const supportContact = ref(null)
const recentConversations = ref([])
const conversationStats = ref({})
const showSelector = ref(false)
const { cachedFetch } = useGhlCache()

const hasAttributionData = computed(() => {
  if (!supportContact.value?.attribution) return false

  const attr = supportContact.value.attribution
  return (
    attr.utm_content ||
    attr.utm_medium ||
    attr.utm_campaign ||
    attr.utm_source ||
    attr.utm_term ||
    attr.source ||
    attr.medium ||
    attr.session_source
  )
})

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

const openContactSelector = () => {
  showSelector.value = true
}

const fetchConversations = async(contactId) => {
  try {
    const response = await cachedFetch(`/ajax/ghl/contacts/${contactId}/conversations?limit=5`)
    const data = await response.json()
    recentConversations.value = data.conversations || []
  } catch (error) {
    // Failed to fetch conversations
  }
}

const fetchConversationStats = async(contactId) => {
  try {
    const response = await cachedFetch(`/ajax/ghl/contacts/${contactId}/stats`)
    const data = await response.json()
    conversationStats.value = data.stats || {}
  } catch (error) {
    // Failed to fetch stats
  }
}

const assignContact = async(contact) => {
  try {
    const response = await fetch(`/ajax/ghl/contacts/${contact.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        assign_as: 'support_contact',
        organization_id: parseInt(props.organizationId, 10)
      })
    })

    if (response.ok) {
      supportContact.value = contact
      await fetchConversations(contact.id)
      await fetchConversationStats(contact.id)
    }
  } catch (error) {
    // Failed to assign contact
  } finally {
    showSelector.value = false
  }
}

const fetchSupportContact = async() => {
  try {
    loading.value = true
    const response = await cachedFetch(`/ajax/organizations/${parseInt(props.organizationId, 10)}`)
    const data = await response.json()

    if (data.support_contact) {
      supportContact.value = data.support_contact
      await fetchConversations(data.support_contact.id)
      await fetchConversationStats(data.support_contact.id)
    }
  } catch (error) {
    // Failed to fetch support contact
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchSupportContact()
})
</script>

<style scoped>
.ghl-contact-card {
  @apply bg-white rounded-lg p-6 shadow-sm;
}
</style>
